import { useState, useCallback } from 'react';
import { Product } from '../../components/placiFy/types';
import { http } from '../../config/http';
import { mapApiProductToProduct, ApiProduct } from '../../utils/productUtils';

interface ApiResponse {
    answer: ApiProduct[] | string;
    message: string;
    suggested_questions?: string[];
    conversation_id?: string;
}

interface UseProductSearchReturn {
    searchProducts: (question: string, selectedProductContext?: Product | null) => Promise<void>;
    products: Product[];
    message: string;
    loading: boolean;
    error: string | null;
    selectedProduct: Product | null;
    setSelectedProduct: (product: Product | null) => void;
    setMessage: (message: string) => void;
    setProducts: (products: Product[]) => void;
    setLoading: (loading: boolean) => void;
    suggestedQuestions: string[];
    resetConversation: () => void;
}

export function useProductSearch(): UseProductSearchReturn {
    const [products, setProducts] = useState<Product[]>([]);
    const [message, setMessage] = useState<string>('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
    const [conversationId, setConversationId] = useState<string | null>(null);
    const [suggestedQuestions, setSuggestedQuestions] = useState<string[]>([]);

    const searchProducts = useCallback(async (question: string, selectedProductContext?: Product | null) => {
        if (!question.trim()) {
            setError('Please enter a question');
            return;
        }

        setLoading(true);
        setError(null);
        setProducts([]);
        setMessage('');
        // Don't clear selectedProduct if we're using product context
        if (!selectedProductContext) {
            setSelectedProduct(null);
        }
        setSuggestedQuestions([]);

        try {
            let response;
            
            // Check if we have a selected product - use product page chat endpoint
            if (selectedProductContext) {
                // Use original API product data if available, otherwise construct from Product
                let productContext;
                
                if (selectedProductContext.apiProductData) {
                    // Use the full API product data with all fields
                    productContext = {
                        code: selectedProductContext.apiProductData.code,
                        name: selectedProductContext.apiProductData.name,
                        designer: selectedProductContext.apiProductData.designer,
                        description: selectedProductContext.apiProductData.description,
                        category: selectedProductContext.apiProductData.category,
                        subcategory: selectedProductContext.apiProductData.subcategory,
                        selected_finish: selectedProductContext.apiProductData.selected_finish,
                        price: selectedProductContext.apiProductData.price,
                        specifications: selectedProductContext.apiProductData.specifications,
                        brand_collection: selectedProductContext.apiProductData.brand_collection,
                        image: selectedProductContext.apiProductData.image,
                        Length: selectedProductContext.apiProductData.Length,
                        Width: selectedProductContext.apiProductData.Width,
                        Height: selectedProductContext.apiProductData.Height,
                        Extension: selectedProductContext.apiProductData.Extension,
                        Wattage: selectedProductContext.apiProductData.Wattage,
                        Weight: selectedProductContext.apiProductData.Weight,
                        Overall_Height: selectedProductContext.apiProductData.Overall_Height,
                        Base: selectedProductContext.apiProductData.Base,
                        Diameter: selectedProductContext.apiProductData.Diameter,
                        score: selectedProductContext.apiProductData.score
                    };
                } else {
                    // Fallback: construct from Product type (for products selected before API data was stored)
                    const categoryParts = (selectedProductContext.category || '').split(' - ');
                    const category = categoryParts[0] || '';
                    const subcategory = categoryParts[1] || '';
                    
                    productContext = {
                        code: selectedProductContext.id,
                        name: selectedProductContext.name,
                        designer: selectedProductContext.designer || '',
                        description: selectedProductContext.description || '',
                        category: category,
                        subcategory: subcategory,
                        selected_finish: '',
                        price: selectedProductContext.price.toString(),
                        specifications: '',
                        brand_collection: selectedProductContext.brand_collection || '',
                        image: selectedProductContext.imageUrl,
                        Length: '',
                        Width: '',
                        Height: '',
                        Extension: '',
                        Wattage: '',
                        Weight: '',
                        Overall_Height: '',
                        Base: '',
                        Diameter: '',
                        score: 0
                    };
                }

                const username = localStorage.getItem('username') || localStorage.getItem('user_name') || '';
                
                // Call product page chat endpoint using http instance (includes auth headers)
                response = await http.post('/product_page_chat', {
                    client_env: 'shoptalk',
                    user_name: username,
                    context: [productContext],
                    question: question
                });
            } else {
                // Use regular endpoint
                const params: any = {
                    client_env: 'shoptalk',
                    bot_name: 'shoptalk',
                    question: question
                };
                
                if (conversationId) {
                    params.conversation_id = conversationId;
                }

                response = await http.post('/ask_query_vc_voice', {}, {
                    params: params
                });
            }

            const data: any = response.data;
            
            // Handle product page chat response format
            if (selectedProductContext && data.response) {
                // Product page chat returns { response: "..." }
                setProducts([]);
                setMessage(data.response || '');
                setSuggestedQuestions([]);
            } else {
                // Regular API response format
                const apiData: ApiResponse = data;
                
                // Store conversation_id from response if present
                if (apiData.conversation_id) {
                    setConversationId(apiData.conversation_id);
                }
                
                // Store suggested questions if available
                if (apiData.suggested_questions && Array.isArray(apiData.suggested_questions) && apiData.suggested_questions.length > 0) {
                    setSuggestedQuestions(apiData.suggested_questions);
                } else {
                    setSuggestedQuestions([]);
                }
                
                if (apiData.answer) {
                    // Check if answer is an array (products) or string (message)
                    if (Array.isArray(apiData.answer)) {
                        const mappedProducts = apiData.answer.map(mapApiProductToProduct);
                        setProducts(mappedProducts);
                        // Always provide a message for speech - use API message or default
                        setMessage(apiData.message || 'Here are some products for you.');
                    } else {
                        // Answer is a string message, no products
                        setProducts([]);
                        setMessage(apiData.answer || apiData.message || '');
                    }
                } else if (apiData.message) {
                    // Just a message, no products
                    setProducts([]);
                    setMessage(apiData.message);
                } else {
                    setError('No response from server');
                }
            }
        } catch (err) {
            console.error('Error fetching products:', err);
            setError(err instanceof Error ? err.message : 'Failed to fetch products');
        } finally {
            setLoading(false);
        }
    }, [conversationId, selectedProduct]);

    const resetConversation = useCallback(() => {
        setConversationId(null);
        setProducts([]);
        setMessage('');
        setError(null);
        setSuggestedQuestions([]);
        setSelectedProduct(null);
    }, []);

    return {
        searchProducts,
        products,
        message,
        loading,
        error,
        selectedProduct,
        setSelectedProduct,
        setMessage,
        setProducts,
        setLoading,
        suggestedQuestions,
        resetConversation,
    };
}

